import { IcConfirmedBold, IcDown20, IcSteady20, IcUp20, IcWrong } from '@assets/icons/20x20';
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { TooltipTypeEnum } from '@components/map/tooltips/tooltip.types';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { selectFlightByDisplayId } from '@redux/intercom/intercom.selector';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectAltitudeUnit, selectHeightSuffix, selectHeightUnit } from '@redux/settings/settings.selectors';
import { selectSelectedTargets } from '@redux/situation/situation.selectors';
import { shouldHideTooltip } from '@utils/map/map.utils';
import { findPopupByType } from '@utils/popup.utils';
import {
  getTargetAltitude,
  getTargetDisplayedId,
  getTargetHeight,
  getTargetReliability,
  getTargetSpeed,
  getTargetTendency,
  isDrone,
} from '@utils/target.utils';
import { convertDistanceUnitFromMeter, convertSpeedUnitFromMPerS, formatUnit, SpeedUnits } from '@utils/unit.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import classNames from 'classnames';
import { FunctionComponent, SVGProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { Popup } from 'react-map-gl/mapbox';

import { InfoDataTypeEnum, PopupTypeEnum } from '@/types/popup.types';
import { TendencyEnum } from '@/types/sensor/sensor.types';

import { useMapContext } from '../MapContext';
import { TooltipComponentProps } from './tooltip.types';

type Props = { height: number | null; altitude: number | null; tendency: TendencyEnum | null };

const tendancyIcon: Record<TendencyEnum, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  [TendencyEnum.STEADY]: IcSteady20,
  [TendencyEnum.UP]: IcUp20,
  [TendencyEnum.DOWN]: IcDown20,
} as const;

function TargetVerticalMetrics({ height, altitude, tendency }: Readonly<Props>) {
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const heightUnit = useAppSelector(selectHeightUnit);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  return (
    <>
      <HStack gap={1}>
        <Text textStyle="screenStd">{`h: ${formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, height))}`}</Text>
        {showHeightSuffix && (
          <Text textStyle="screenStd" color="neutral.500">
            <FormattedMessage id="target.agl" />
          </Text>
        )}
      </HStack>
      <HStack gap={1}>
        <Text textStyle="screenStd">
          {`alt: ${formatUnit(altitudeUnit, convertDistanceUnitFromMeter(altitudeUnit, altitude))}`}
        </Text>
        {showHeightSuffix && (
          <Text textStyle="screenStd" color="neutral.500">
            <FormattedMessage id="target.amsl" />
          </Text>
        )}
      </HStack>
      {tendency !== null && (
        <Icon as={tendancyIcon[tendency]} color="neutral.white" boxSize={{ base: '16px', '4k': '20px' }} />
      )}
    </>
  );
}

export default function TargetTooltip({
  data,
  isPermanent = false,
}: Readonly<TooltipComponentProps<TooltipTypeEnum.TARGET> & { isPermanent?: boolean }>) {
  const { isMeasureActive } = useMapContext();
  const height = getTargetHeight(data);
  const altitude = getTargetAltitude(data);
  const tendency = getTargetTendency(data);
  const speed = getTargetSpeed(data);
  const flight = useAppSelector((state) => selectFlightByDisplayId(state, data.displayId));
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const organization = useAppSelector(selectActiveOrganization);

  const [long, lat] = toLonLatArray(data.lastPosition.location!.position, false)!;
  const selectedTargets = useSelectorWithMapId(selectSelectedTargets);
  const isSelected = selectedTargets.some((target) => target.id === data.id);
  const speedInKnots = formatUnit(SpeedUnits.KNOTS, convertSpeedUnitFromMPerS(SpeedUnits.KNOTS, speed, 0));
  const speedInKilometersPerHour = formatUnit(
    SpeedUnits.KILOMETERS_PER_HOUR,
    convertSpeedUnitFromMPerS(SpeedUnits.KILOMETERS_PER_HOUR, speed, 0),
  );
  const speedInMilesPerHour = formatUnit(
    SpeedUnits.MILES_PER_HOUR,
    convertSpeedUnitFromMPerS(SpeedUnits.MILES_PER_HOUR, speed, 0),
  );

  const hidden =
    shouldHideTooltip(findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_TARGET), data) ||
    isMeasureActive;

  const tooltipZIndex = isPermanent ? 1 : 2;

  return (
    <Popup
      className={classNames('mapbox-popup-target', { hidden })}
      offset={0}
      latitude={lat}
      longitude={long}
      closeButton={false}
      closeOnClick={false}
      style={{
        zIndex: isSelected ? 3 : tooltipZIndex,
      }}
    >
      <VStack
        backgroundColor="neutral.800"
        borderBottom="2px solid"
        borderColor="neutral.white"
        padding="standardXS"
        transform="translate(50px, calc(-100% - 50px))"
        minWidth="max-content"
        gap="tightS"
        alignItems="flex-start"
        position="absolute"
      >
        <Box
          as="span"
          position="absolute"
          left="0"
          bottom="-2px"
          zIndex={-3}
          width="55px"
          height="2px"
          bg="neutral.white"
          transform={`rotate(135deg)`}
          transformOrigin="left center"
        />
        <Text
          textStyle="screenStd"
          fontWeight="medium"
          lineClamp={1}
          backgroundColor="neutral.black"
          paddingY={0.25}
          paddingX={0.5}
        >
          {getTargetDisplayedId(data)}
        </Text>
        {flight && <Text textStyle="screenStd">{flight.aircraftType}</Text>}
        <HStack gap="standardXS">
          <TargetVerticalMetrics height={height} altitude={altitude} tendency={tendency} />
        </HStack>
        {speed !== null && (
          <HStack gap={1}>
            <Text textStyle="screenStd">{speedInKnots}</Text>
            <Text textStyle="screenStd">{`(${speedInKilometersPerHour} / ${speedInMilesPerHour})`}</Text>
          </HStack>
        )}
        {isDrone(data, organization) && (
          <Text textStyle="screenStd">
            <FormattedMessage id={`target.reliability.${getTargetReliability(data)}`} />
          </Text>
        )}
        {flight !== null && !isPermanent && (
          <HStack gap="tightM" width="100%" align="start">
            <Text textStyle="screenStd">{flight.departure}</Text>
            <Text textStyle="screenStd">-</Text>
            <Text textStyle="screenStd" textDecoration={flight.reroutedArrival === null ? undefined : 'line-through'}>
              {flight.arrival}
            </Text>
            {flight.reroutedArrival && <Text textStyle="screenStd">{flight.reroutedArrival}</Text>}
            <Icon
              as={flight.currentArrivalBooked ? IcConfirmedBold : IcWrong}
              boxSize={{ base: '16px', '4k': '20px' }}
              color={flight.currentArrivalBooked ? 'green.500' : 'red.500'}
            />
          </HStack>
        )}
      </VStack>
    </Popup>
  );
}
